import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import ListItem from "@mui/material/ListItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { CustomLink } from "core/components/CustomLink";
import { TagsDisplay } from "core/components/TagsDisplay";
import { useBreakpoints } from "core/hooks/useBreakpoints";
import { usePreloadDmsNode } from "features/DMS/hooks/usePreloadDmsNode";
import type { ReactNode } from "react";
import type { SharedDMSWidgetListItemProps } from "./defs";

interface DMSWidgetListItemTemplateProps
  extends Partial<SharedDMSWidgetListItemProps> {
  AvatarSlot: ReactNode;
  TitleSlot: ReactNode;
  ButtonSlot: ReactNode;
}

export function DMSWidgetListItemTemplate({
  AvatarSlot,
  TitleSlot,
  ButtonSlot,
  parents = [],
  dateLabel,
  dateText,
  tags,
}: DMSWidgetListItemTemplateProps) {
  const { isMobile } = useBreakpoints();
  const preloadNode = usePreloadDmsNode();

  const ParentBreadCrumbs = (
    <Breadcrumbs
      sx={{
        pl: isMobile ? 1 : 0,
      }}
      separator=">"
    >
      {parents
        .filter(({ href }) => Boolean(href))
        .map(({ id, name, href, path }) =>
          href ? (
            <CustomLink
              sx={{ lineHeight: 1 }}
              variant="caption"
              fontWeight={500}
              key={id}
              href={href}
              onPointerOver={() => {
                preloadNode(path!, "Library", "view");
              }}
            >
              {name}
            </CustomLink>
          ) : (
            <Typography sx={{ lineHeight: 1 }} key={id} variant="overline">
              {name}
            </Typography>
          )
        )}
      <span />
    </Breadcrumbs>
  );

  if (isMobile) {
    return (
      <ListItem
        disablePadding
        disableGutters
        sx={{
          pl: 1,
          py: 1,
          gap: 1,
          "&:not(:last-of-type)": {
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <Stack alignItems="flex-start" flex={1}>
          {ParentBreadCrumbs}
          <Stack flex={1} direction="row" alignItems="center" spacing={2}>
            {AvatarSlot}
            <Stack>
              {TitleSlot}
              <Stack direction="row" spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  {dateLabel}: {dateText}
                </Typography>
              </Stack>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                fontStyle="italic"
              >
                {tags?.map((tag) => `#${tag}`).join(", ")}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
        <Box
          sx={{
            width: "75px",
            borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
            alignSelf: "stretch",
            display: "grid",
          }}
        >
          {ButtonSlot}
        </Box>
      </ListItem>
    );
  }

  return (
    <ListItem
      disablePadding
      sx={{
        py: 0.5,
        display: "grid",
        alignItems: "center",
        gridTemplateColumns: "auto 3fr 1fr 1fr 150px",
        columnGap: 4,
        rowGap: 2,
        "&:not(:last-of-type)": {
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <Box>{AvatarSlot}</Box>
      <Stack>
        {ParentBreadCrumbs}
        {TitleSlot}
      </Stack>
      <TagsDisplay tags={typeof tags === "string" ? [tags] : (tags ?? [])} />
      <Stack>
        <Typography variant="body2" color="text.secondary">
          {dateLabel}
        </Typography>
        <Typography variant="body2" fontWeight="bold">
          {dateText}
        </Typography>
      </Stack>
      <Box
        display="grid"
        height="100%"
        justifySelf="stretch"
        sx={{
          placeItems: "stretch",
          borderLeft: (theme) =>
            isMobile ? undefined : `1px solid ${theme.palette.divider}`,
        }}
      >
        {ButtonSlot}
      </Box>
    </ListItem>
  );
}
